/**
 * PackOpeningOverlay — 卡包開啟動畫
 *
 * Full-screen overlay that plays after a successful pack purchase.
 * Uses pure CSS animations (no Phaser dependency).
 *
 * Flow: pack_display → opening → cards_spread → revealing → done
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import type { CardDefinition } from '../../services/ShopAPI';

// ─── Types ────────────────────────────────────────────────────────

interface PackOpeningProps {
  cards: CardDefinition[];
  onComplete: () => void;
}

type AnimStage = 'pack_display' | 'opening' | 'cards_spread' | 'revealing' | 'done';

// ─── Constants ────────────────────────────────────────────────────

const RARITY_GLOW: Record<string, string> = {
  SSR: '#ffd700',
  SR: '#c77dff',
  R: '#4ea8de',
  N: 'rgba(255,255,255,0.25)',
};

const RARITY_LABELS: Record<string, string> = {
  SSR: '✦ SSR',
  SR: '★ SR',
  R: '◆ R',
  N: '○ N',
};

const RARITY_COLORS: Record<string, string> = {
  SSR: '#ffd700',
  SR: '#c77dff',
  R: '#4ea8de',
  N: '#aaaaaa',
};

/** Spread positions for 5 cards (percentage offsets from center). */
const CARD_POSITIONS: Array<{ x: number; y: number }> = [
  { x: -36, y: -4 },
  { x: -18, y: 2 },
  { x: 0, y: -6 },
  { x: 18, y: 2 },
  { x: 36, y: -4 },
];

// ─── Inject keyframe animations once ─────────────────────────────

let stylesInjected = false;

function injectStyles() {
  if (stylesInjected) return;
  stylesInjected = true;

  const css = `
    @keyframes po-packEnter {
      from { transform: scale(0) rotate(-8deg); opacity: 0; }
      to   { transform: scale(1) rotate(0deg);  opacity: 1; }
    }
    @keyframes po-packPulse {
      0%, 100% { box-shadow: 0 0 30px rgba(232,210,130,0.4), inset 0 0 20px rgba(232,210,130,0.1); }
      50%      { box-shadow: 0 0 50px rgba(232,210,130,0.7), inset 0 0 30px rgba(232,210,130,0.2); }
    }
    @keyframes po-packBurst {
      0%   { transform: scale(1); opacity: 1; }
      100% { transform: scale(2.5); opacity: 0; }
    }
    @keyframes po-cardFlyOut {
      from { transform: translate(0px, 0px) scale(0.2); opacity: 0; }
      to   { opacity: 1; }
    }
    @keyframes po-cardFlip {
      0%   { transform: rotateY(180deg); }
      100% { transform: rotateY(0deg); }
    }
    @keyframes po-ssrFlash {
      0%   { opacity: 0; }
      15%  { opacity: 0.85; }
      100% { opacity: 0; }
    }
    @keyframes po-srFlash {
      0%   { opacity: 0; }
      15%  { opacity: 0.5; }
      100% { opacity: 0; }
    }
    @keyframes po-glowPulseGold {
      0%, 100% { box-shadow: 0 0 18px 4px rgba(255,215,0,0.5); }
      50%      { box-shadow: 0 0 36px 8px rgba(255,215,0,0.85); }
    }
    @keyframes po-glowPulsePurple {
      0%, 100% { box-shadow: 0 0 14px 3px rgba(199,125,255,0.4); }
      50%      { box-shadow: 0 0 28px 6px rgba(199,125,255,0.7); }
    }
    @keyframes po-glowPulseBlue {
      0%, 100% { box-shadow: 0 0 10px 2px rgba(78,168,222,0.3); }
      50%      { box-shadow: 0 0 20px 4px rgba(78,168,222,0.55); }
    }
    @keyframes po-fadeIn {
      from { opacity: 0; transform: translateY(12px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes po-shimmer {
      0%   { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }
    @keyframes po-particleFly {
      0%   { opacity: 1; transform: translate(0,0) scale(1); }
      100% { opacity: 0; transform: translate(var(--po-dx), var(--po-dy)) scale(0); }
    }
  `;

  const style = document.createElement('style');
  style.textContent = css;
  document.head.appendChild(style);
}

// ─── Component ────────────────────────────────────────────────────

export function PackOpeningOverlay({ cards, onComplete }: PackOpeningProps) {
  const [stage, setStage] = useState<AnimStage>('pack_display');
  const [revealedCards, setRevealedCards] = useState<Set<number>>(new Set());
  const [flashRarity, setFlashRarity] = useState<string | null>(null);
  const autoRevealTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Inject CSS on mount
  useEffect(() => {
    injectStyles();
  }, []);

  // Auto-reveal remaining cards after 6 seconds in cards_spread
  useEffect(() => {
    if (stage === 'cards_spread') {
      autoRevealTimer.current = setTimeout(() => {
        revealAllCards();
      }, 8000);
    }
    return () => {
      if (autoRevealTimer.current) clearTimeout(autoRevealTimer.current);
    };
  }, [stage]);

  // When all cards revealed, move to done after a brief pause
  useEffect(() => {
    if (stage === 'revealing' && revealedCards.size === cards.length) {
      const t = setTimeout(() => { setStage('done'); }, 600);
      return () => { clearTimeout(t); };
    }
  }, [stage, revealedCards.size, cards.length]);

  // ── Handlers ──

  const handlePackClick = useCallback(() => {
    if (stage !== 'pack_display') return;
    setStage('opening');
    // After burst animation, spread cards
    setTimeout(() => { setStage('cards_spread'); }, 600);
  }, [stage]);

  const revealCard = useCallback((index: number) => {
    if (revealedCards.has(index)) return;
    const card = cards[index];
    // SSR / SR flash
    if (card.rarity === 'SSR' || card.rarity === 'SR') {
      setFlashRarity(card.rarity);
      setTimeout(() => { setFlashRarity(null); }, 700);
    }
    setRevealedCards((prev) => {
      const next = new Set(prev);
      next.add(index);
      if (next.size === cards.length) {
        setStage('revealing'); // triggers done after pause
      }
      return next;
    });
  }, [revealedCards, cards]);

  const revealAllCards = useCallback(() => {
    if (autoRevealTimer.current) clearTimeout(autoRevealTimer.current);

    // Find highest rarity for flash
    const highest = cards.reduce((best, c) => {
      const rank = { SSR: 4, SR: 3, R: 2, N: 1 } as Record<string, number>;
      return (rank[c.rarity] ?? 0) > (rank[best] ?? 0) ? c.rarity : best;
    }, 'N');
    if (highest === 'SSR' || highest === 'SR') {
      setFlashRarity(highest);
      setTimeout(() => { setFlashRarity(null); }, 700);
    }

    const all = new Set(cards.map((_, i) => i));
    setRevealedCards(all);
    setStage('revealing');
  }, [cards]);

  const handleSkip = useCallback(() => {
    if (autoRevealTimer.current) clearTimeout(autoRevealTimer.current);
    const all = new Set(cards.map((_, i) => i));
    setRevealedCards(all);
    setStage('done');
  }, [cards]);

  // ── Render ──

  return (
    <div style={styles.overlay}>
      {/* Skip button */}
      <button style={styles.skipButton} onClick={handleSkip}>
        跳過 ▸
      </button>

      {/* Full-screen flash effects */}
      {flashRarity === 'SSR' && <div style={styles.ssrFlash} />}
      {flashRarity === 'SR' && <div style={styles.srFlash} />}

      {/* Pack display & burst */}
      {(stage === 'pack_display' || stage === 'opening') && (
        <div
          style={{
            ...styles.pack,
            animation:
              stage === 'pack_display'
                ? 'po-packEnter 0.5s ease-out forwards, po-packPulse 2s ease-in-out infinite 0.5s'
                : 'po-packBurst 0.5s ease-out forwards',
            cursor: stage === 'pack_display' ? 'pointer' : 'default',
          }}
          onClick={handlePackClick}
        >
          <div style={styles.packInner}>
            <div style={styles.packIcon}>🎴</div>
            <div style={styles.packLabel}>星靈王卡包</div>
            {stage === 'pack_display' && (
              <div style={styles.packHint}>點擊開啟</div>
            )}
          </div>
        </div>
      )}

      {/* Cards spread */}
      {(stage === 'cards_spread' || stage === 'revealing' || stage === 'done') && (
        <div style={styles.cardsContainer}>
          {cards.map((card, i) => {
            const pos = CARD_POSITIONS[i] ?? { x: 0, y: 0 };
            const isRevealed = revealedCards.has(i);
            const glowColor = RARITY_GLOW[card.rarity] ?? RARITY_GLOW.N;
            const glowAnim =
              card.rarity === 'SSR'
                ? 'po-glowPulseGold 1.5s ease-in-out infinite'
                : card.rarity === 'SR'
                ? 'po-glowPulsePurple 1.8s ease-in-out infinite'
                : card.rarity === 'R'
                ? 'po-glowPulseBlue 2s ease-in-out infinite'
                : 'none';

            return (
              <div
                key={`${card.code}-${String(i)}`}
                style={{
                  ...styles.cardWrapper,
                  left: `calc(50% + ${String(pos.x)}vw - 60px)`,
                  top: `calc(50% + ${String(pos.y)}vh - 84px)`,
                  animation: `po-cardFlyOut 0.5s ease-out ${String(i * 0.1)}s both`,
                }}
                onClick={() => {
                  if (!isRevealed && (stage === 'cards_spread' || stage === 'revealing')) {
                    revealCard(i);
                  }
                }}
              >
                {/* Glow ring behind card */}
                <div
                  style={{
                    ...styles.cardGlow,
                    animation: glowAnim,
                    boxShadow: `0 0 14px 3px ${glowColor}`,
                  }}
                />
                {/* Card flip container */}
                <div
                  style={{
                    ...styles.cardFlipContainer,
                    transform: isRevealed ? 'rotateY(0deg)' : 'rotateY(180deg)',
                    transition: 'transform 0.6s ease',
                  }}
                >
                  {/* Front (revealed) */}
                  <div
                    style={{
                      ...styles.cardFace,
                      ...styles.cardFront,
                      borderColor: RARITY_COLORS[card.rarity] ?? '#555',
                      backgroundColor:
                        card.rarity === 'SSR'
                          ? 'rgba(255, 215, 0, 0.12)'
                          : card.rarity === 'SR'
                          ? 'rgba(199, 125, 255, 0.08)'
                          : '#2a2a4a',
                    }}
                  >
                    {card.image_url ? (
                      <div
                        style={{
                          width: '100%',
                          height: '60px',
                          borderRadius: '4px',
                          overflow: 'hidden',
                          marginBottom: '4px',
                        }}
                      >
                        <img
                          src={card.image_url}
                          alt={card.name}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                        />
                      </div>
                    ) : (
                      <div style={styles.cardArtPlaceholder}>
                        {card.rarity === 'SSR' ? '🌟' : card.rarity === 'SR' ? '⭐' : '🃏'}
                      </div>
                    )}
                    <div
                      style={{
                        ...styles.cardRarityBadge,
                        color: RARITY_COLORS[card.rarity] ?? '#aaa',
                      }}
                    >
                      {RARITY_LABELS[card.rarity] ?? card.rarity}
                    </div>
                    <div style={styles.cardName}>{card.name}</div>
                    <div style={styles.cardFaction}>{card.faction_zh || card.faction}</div>
                  </div>

                  {/* Back (hidden) */}
                  <div style={{ ...styles.cardFace, ...styles.cardBack }}>
                    <div style={styles.cardBackPattern}>
                      <div style={styles.cardBackLogo}>星</div>
                      <div style={styles.cardBackSub}>靈王</div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Continue button */}
      {stage === 'done' && (
        <button
          style={styles.continueButton}
          onClick={onComplete}
        >
          繼續
        </button>
      )}
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    zIndex: 2000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'Arial, sans-serif',
    color: '#ffffff',
    overflow: 'hidden',
  },

  // Skip
  skipButton: {
    position: 'absolute',
    top: '16px',
    right: '20px',
    padding: '8px 18px',
    fontSize: '14px',
    backgroundColor: 'rgba(255,255,255,0.1)',
    color: '#aaa',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: '6px',
    cursor: 'pointer',
    zIndex: 2010,
    fontFamily: 'Arial, sans-serif',
  },

  // Flash effects
  ssrFlash: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(255, 215, 0, 0.3)',
    pointerEvents: 'none' as const,
    animation: 'po-ssrFlash 0.7s ease-out forwards',
    zIndex: 2005,
  },
  srFlash: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(199, 125, 255, 0.25)',
    pointerEvents: 'none' as const,
    animation: 'po-srFlash 0.6s ease-out forwards',
    zIndex: 2005,
  },

  // Pack
  pack: {
    width: '180px',
    height: '250px',
    borderRadius: '14px',
    background: 'linear-gradient(145deg, #3a2a6a, #252545, #3a2a6a)',
    border: '2px solid #e8d282',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative' as const,
  },
  packInner: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: '8px',
  },
  packIcon: {
    fontSize: '56px',
    lineHeight: 1,
  },
  packLabel: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#e8d282',
    textShadow: '0 0 10px rgba(232,210,130,0.5)',
  },
  packHint: {
    fontSize: '13px',
    color: '#aaa',
    marginTop: '8px',
    animation: 'po-fadeIn 0.6s ease-out 0.6s both',
  },

  // Cards
  cardsContainer: {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    pointerEvents: 'none' as const,
  },
  cardWrapper: {
    position: 'absolute' as const,
    width: '120px',
    height: '168px',
    perspective: '800px',
    cursor: 'pointer',
    pointerEvents: 'auto' as const,
  },
  cardGlow: {
    position: 'absolute' as const,
    top: '-4px',
    left: '-4px',
    right: '-4px',
    bottom: '-4px',
    borderRadius: '12px',
    pointerEvents: 'none' as const,
    zIndex: -1,
  },
  cardFlipContainer: {
    width: '100%',
    height: '100%',
    position: 'relative' as const,
    transformStyle: 'preserve-3d' as const,
  },

  // Card faces
  cardFace: {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    borderRadius: '10px',
    backfaceVisibility: 'hidden' as const,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  cardFront: {
    border: '2px solid',
    padding: '8px 6px',
    gap: '2px',
  },
  cardBack: {
    transform: 'rotateY(180deg)',
    background: 'linear-gradient(135deg, #1a1a4e, #2a2a6e, #1a1a4e)',
    border: '2px solid #4a4a8a',
  },
  cardBackPattern: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    gap: '2px',
  },
  cardBackLogo: {
    fontSize: '36px',
    fontWeight: 'bold',
    color: '#6a6aaa',
    textShadow: '0 0 12px rgba(106,106,170,0.5)',
  },
  cardBackSub: {
    fontSize: '14px',
    color: '#5a5a9a',
    letterSpacing: '4px',
  },

  // Card front content
  cardArtPlaceholder: {
    fontSize: '28px',
    marginBottom: '4px',
  },
  cardRarityBadge: {
    fontSize: '12px',
    fontWeight: 'bold',
  },
  cardName: {
    fontSize: '12px',
    fontWeight: 'bold',
    textAlign: 'center' as const,
    lineHeight: '1.2',
    maxHeight: '30px',
    overflow: 'hidden',
  },
  cardFaction: {
    fontSize: '10px',
    color: '#999',
    marginTop: '2px',
  },

  // Continue button
  continueButton: {
    position: 'absolute' as const,
    bottom: '60px',
    left: '50%',
    transform: 'translateX(-50%)',
    padding: '14px 48px',
    fontSize: '18px',
    backgroundColor: '#e8d282',
    color: '#1a1a2e',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontFamily: 'Arial, sans-serif',
    animation: 'po-fadeIn 0.4s ease-out',
    zIndex: 2010,
  },
};
